import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { absoluteTime } from "../../lib/time";
import { currency as money } from "../../lib/money";
import type { CompanyCredit, CreditReport } from "../../lib/crm-types";
import { Overlay } from "../ui/Overlay";
import { cn } from "../ui/cn";

/**
 * The credit report, read from the copy we already paid for.
 *
 * **Nothing here orders anything.** Opening this renders the stored report; the
 * only way to spend money is the Refresh button on the panel behind it. That is
 * the whole reason a report is archived rather than fetched on view -- a rep
 * flicking through an account's history should not be billing the company per
 * glance.
 *
 * What it shows is the fraction of a Creditsafe report a salesperson acts on:
 * the rating and limit, who the directors are, and three years of headline
 * figures. The full report -- CCJs, payment behaviour, mortgage charges, group
 * structure -- runs to forty sections, and re-implementing those in a side panel
 * would be a worse version of a tool the reader already has a login for. The
 * header links straight through to it.
 */
export function CreditReportDialog({
  companyId,
  companyName,
  credit,
  onClose,
}: {
  companyId: number;
  companyName: string;
  credit: CompanyCredit;
  onClose: () => void;
}) {
  const report = credit.report;

  const history = useQuery({
    queryKey: queryKeys.crmCreditHistory(companyId),
    queryFn: () => crmApi.creditHistory(companyId),
    staleTime: 60_000,
  });

  return (
    <Overlay onClose={onClose}>
      <header className="pr-8">
        <h2 className="text-sm font-semibold text-content">
          Credit report — {report?.identity.name ?? companyName}
        </h2>
        <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-faint">
          <span>Creditsafe {credit.connectId}</span>
          {credit.safeNo ? <span>Safe no. {credit.safeNo}</span> : null}
          {credit.orderedAt ? (
            <span className={cn(credit.stale && "text-sla-warning")}>
              Ordered {absoluteTime(credit.orderedAt)}
              {credit.orderedBy ? ` by ${credit.orderedBy.name}` : ""}
            </span>
          ) : null}
          <a
            href={credit.reportUrl}
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700"
          >
            Open the full report in Creditsafe ↗
          </a>
        </p>
      </header>

      {!report ? (
        <p className="mt-4 text-xs text-faint">
          This company is linked to Creditsafe, but no report has been ordered
          yet. Use Refresh on the Financials panel to order one.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-5">
          {/*
            Said first and said loudly. A report Creditsafe could not fully build
            has gaps that look identical to genuine zeroes, and somebody reading
            "no negative information" off a section that failed to load is the
            worst outcome this dialog can produce.
          */}
          {report.failedSections.length > 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              Creditsafe could not build{" "}
              {report.failedSections.join(", ")}. Anything those sections would
              have carried is missing here, not absent from the company.
            </p>
          ) : null}

          <Rating report={report} />
          <Identity report={report} />
          <Accounts report={report} />
          <Directors report={report} />

          {history.data && history.data.reports.length > 1 ? (
            <History rows={history.data.reports} />
          ) : null}
        </div>
      )}
    </Overlay>
  );
}

/** The rating, the limit and what Creditsafe calls the risk. */
function Rating({ report }: { report: CreditReport }) {
  const { credit } = report;
  const currency = credit.currency ?? "GBP";

  return (
    <section>
      <SectionHeading>Rating</SectionHeading>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Rating"
          value={credit.creditRating}
          /* The A–E band is standardised across countries, so it is the figure
             worth showing largest; the local score sits under it. */
          hint={credit.ratingDescription}
          big
        />
        <Stat label="Score" value={numberOf(credit.creditScore)} hint="Out of 100" />
        <Stat
          label="Credit limit"
          value={money(credit.creditLimit, currency)}
          hint="Advisable at any one time"
        />
        <Stat
          label="Default risk"
          value={
            credit.probabilityOfDefault === null
              ? null
              : `${credit.probabilityOfDefault}%`
          }
          hint="Within 12 months"
        />
      </div>
    </section>
  );
}

function Identity({ report }: { report: CreditReport }) {
  const { identity } = report;

  const rows: [string, string | null][] = [
    ["Registered name", identity.name],
    ["Status", identity.status],
    ["Company number", identity.companyNumber],
    ["VAT number", identity.vatNumber],
    ["Legal form", identity.legalForm],
    ["Incorporated", identity.incorporatedOn],
    ["Main activity", identity.activity],
    ["Phone", identity.phone],
  ];

  return (
    <section>
      <SectionHeading>Company</SectionHeading>
      <dl className="divide-y divide-line rounded-md border border-line">
        {rows.map(([label, value]) => (
          <div key={label} className="flex gap-2 px-3 py-1.5 text-xs">
            <dt className="w-36 shrink-0 text-faint">{label}</dt>
            <dd className={cn("min-w-0 flex-1 break-words", value ? "text-content" : "text-faint")}>
              {value ?? "—"}
            </dd>
          </div>
        ))}
        {report.addresses.map((address) => (
          <div key={address.value} className="flex gap-2 px-3 py-1.5 text-xs">
            <dt className="w-36 shrink-0 text-faint">
              {address.type ?? "Address"}
            </dt>
            <dd className="min-w-0 flex-1 break-words text-content">
              {address.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * Filed accounts, newest first.
 *
 * A table rather than a chart: three or four rows of five figures is not a
 * trend, and drawing one would imply a shape the data does not support. The
 * year end is the first column because everything else is meaningless without
 * knowing how stale it is.
 */
function Accounts({ report }: { report: CreditReport }) {
  if (report.accounts.length === 0) {
    return (
      <section>
        <SectionHeading>Filed accounts</SectionHeading>
        <p className="text-[11px] text-faint">
          Creditsafe holds no filed accounts for this company. Normal for a sole
          trader, and for a company incorporated within the last year or so.
        </p>
      </section>
    );
  }

  const columns: [string, (row: CreditReport["accounts"][number]) => string | null][] = [
    ["Year end", (row) => row.yearEndDate],
    ["Revenue", (row) => money(row.revenue, row.currency ?? "GBP")],
    ["Profit before tax", (row) => money(row.profitBeforeTax, row.currency ?? "GBP")],
    ["Total assets", (row) => money(row.totalAssets, row.currency ?? "GBP")],
    ["Shareholders' funds", (row) => money(row.shareholdersFunds, row.currency ?? "GBP")],
    ["Employees", (row) => numberOf(row.employees)],
  ];

  return (
    <section>
      <SectionHeading>Filed accounts</SectionHeading>
      {/* Its own scroller: six money columns do not fit a phone, and the page
          behind this must never scroll sideways because of one table. */}
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[36rem] text-xs">
          <thead>
            <tr className="border-b border-line text-left text-[11px] text-faint">
              {columns.map(([label]) => (
                <th key={label} className="px-3 py-1.5 font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {report.accounts.slice(0, 5).map((row, index) => (
              <tr key={row.yearEndDate ?? index}>
                {columns.map(([label, read]) => {
                  const value = read(row);
                  return (
                    <td
                      key={label}
                      className={cn(
                        "px-3 py-1.5 tabular-nums",
                        value ? "text-content" : "text-faint",
                      )}
                    >
                      {value ?? "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Directors({ report }: { report: CreditReport }) {
  if (report.directors.length === 0) return null;

  const current = report.directors.filter((director) => !director.resignedOn);
  const previous = report.directors.filter((director) => director.resignedOn);

  return (
    <section>
      <SectionHeading>
        Directors{current.length > 0 ? ` (${current.length})` : ""}
      </SectionHeading>
      <ul className="divide-y divide-line rounded-md border border-line">
        {[...current, ...previous.slice(0, 5)].map((director, index) => (
          <li
            key={`${director.name}-${index}`}
            className="flex flex-wrap items-baseline gap-x-2 px-3 py-1.5 text-xs"
          >
            <span
              className={cn(
                director.resignedOn ? "text-faint line-through" : "text-content",
              )}
            >
              {director.name}
            </span>
            {director.position ? (
              <span className="text-[11px] text-muted">{director.position}</span>
            ) : null}
            <span className="ml-auto text-[11px] text-faint">
              {director.resignedOn
                ? `Resigned ${director.resignedOn}`
                : director.appointedOn
                  ? `Since ${director.appointedOn}`
                  : ""}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Every report ordered here.
 *
 * The payoff for archiving rather than overwriting: "their rating dropped —
 * when?" is answerable from reports already paid for, and shown only once there
 * is more than one, since a single row is not a history.
 */
function History({
  rows,
}: {
  rows: { id: number; orderedAt: string; orderedBy: { name: string } | null; creditRating: string | null; creditScore: number | null }[];
}) {
  return (
    <section>
      <SectionHeading>Previous checks</SectionHeading>
      <ul className="divide-y divide-line rounded-md border border-line">
        {rows.map((row) => (
          <li key={row.id} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
            <span className="text-content">{absoluteTime(row.orderedAt)}</span>
            <span className="text-muted">
              {row.creditRating ?? "—"}
              {row.creditScore === null ? "" : ` (${row.creditScore})`}
            </span>
            {row.orderedBy ? (
              <span className="ml-auto text-[11px] text-faint">
                {row.orderedBy.name}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-faint">
      {children}
    </h3>
  );
}

function Stat({
  label,
  value,
  hint,
  big,
}: {
  label: string;
  value: string | null;
  hint?: string | null;
  big?: boolean;
}) {
  return (
    <div className="rounded-md border border-line px-3 py-2">
      <p className="text-[11px] text-faint">{label}</p>
      <p
        className={cn(
          "tabular-nums",
          big ? "text-lg font-semibold" : "text-sm",
          value ? "text-content" : "text-faint",
        )}
      >
        {value ?? "—"}
      </p>
      {hint ? <p className="text-[11px] text-faint">{hint}</p> : null}
    </div>
  );
}

/**
 * Whole units in the report's own currency.
 *
 * The currency is passed in rather than assumed: a report on an Irish or French
 * company is not denominated in pounds, and a euro figure rendered with a £ is
 * a wrong number rather than a cosmetic slip.
 */

function numberOf(value: number | null | undefined): string | null {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : null;
}
